import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useGroupContext } from "groopit-js";
import {
  Button,
  Menu,
  MenuItem,
  Theme,
  useMediaQuery,
} from "@material-ui/core";
import { Clear, GetApp } from "@material-ui/icons";
import { FilterDescription } from "./FilterDescription";
import { useFilterContext } from "./State/FilterContext";
import { useFilterDispatchContext } from "./State/FilterDispatchContext";
import { SearchInput } from "./SearchInput";
import { SavedFilterPicker } from "./SavedFilterPicker";
import { SaveFilterButton } from "./SaveFilterButton";
import {
  doesFilterContainsStaleFilters,
  isDefaultFilter,
  isLegacyFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "./GroopitFilterDTO";
import { AssignmentsFilter } from "./FormPicker/AssignmentsFilter";
import {
  createFilterAddKeywordQueryAction,
  createFilterResetFilterAction,
} from "./State/FilterActions";
import { NamedGroopitFilterDisplayDTO } from "groopit-js/client";
import { useFeedContext } from "./State/FeedContext";
import {
  TypingIndicator,
  TypingIndicatorVariant,
} from "../Icons/TypingIndicator";
import equal from "fast-deep-equal";
import { GroopitFilterDisplay } from "../Models/GroopitFilterDisplay";

interface FeedFilterProps {
  onClose?: () => void;
}

export const Filter = (props: FeedFilterProps) => {
  const { onClose } = props;
  const isLargeScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("lg"),
  );
  const { group, owner, users } = useGroupContext();
  const { filter, filterMode, savedFilters } = useFilterContext();
  const { filterDispatcher, setFilterMode } = useFilterDispatchContext();
  const feed = useFeedContext();
  const [saveDataMenuAnchor, setSaveDataMenuAnchor] =
    useState<null | HTMLElement>(null);
  const saveDataMenuOpen = Boolean(saveDataMenuAnchor);
  const downloadDataForm = useRef<HTMLFormElement>(null);

  const filterContainsStaleFilters = useMemo(
    () => doesFilterContainsStaleFilters(filter, group, users),
    [filter, group, users],
  );

  const isFilterModified = useMemo(() => {
    if (filter instanceof UnnamedGroopitFilterDisplayDTO) {
      return false;
    }

    const savedFilter = savedFilters?.find((f) => f.id === filter.id);
    if (!savedFilter) {
      return false;
    }

    return !equal(filter.filter, savedFilter.filter);
  }, [filter, savedFilters]);

  const downloadData = useCallback(
    (format: string) => {
      if (downloadDataForm.current === null) {
        return;
      }
      downloadDataForm.current.action = `/Groop/Download/${group.id}?format=${format}`;
      downloadDataForm.current.submit();
    },
    [group.id],
  );

  return (
    <>
      {isLargeScreen && filterMode !== "edit" && (
        <>
          <div className="border-b border-app-gray-08 p-4">
            {/* We don't display the chits in the large UI because the filter
                  description is visible. While its technically a multi-select
                  because of this it only ever reports a single value. */}
            <SearchInput
              values={[]}
              onChange={(value) =>
                filterDispatcher(createFilterAddKeywordQueryAction(value[0]))
              }
            />
          </div>
          <div className="border-b border-app-gray-08 p-4 space-y-2">
            {(!isDefaultFilter(filter) ||
              filter instanceof NamedGroopitFilterDisplayDTO) && (
              <div className="text-sm">
                <span>
                  {filter instanceof NamedGroopitFilterDisplayDTO
                    ? `"${filter.name}"${isFilterModified ? " (modified)" : ""}`
                    : "Results"}{" "}
                </span>
                <span>
                  (
                  {feed.loading ? (
                    <TypingIndicator variant={TypingIndicatorVariant.Small} />
                  ) : (
                    <span>{feed.totalCount}</span>
                  )}
                  )
                </span>
              </div>
            )}
            {!filterContainsStaleFilters ? (
              <>
                <FilterDescription />
                {isLegacyFilter(filter) && (
                  <p className="text-xs">
                    Currently showing legacy filters. Note that opening the
                    filter menu will clear the legacy filters.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs">
                <i>Note:</i> this saved filter uses fields that are no longer
                part of the data model. Please click the filter button, edit the
                filter, and resave it.
              </p>
            )}
            <div className="flex gap-2">
              {!isDefaultFilter(filter) && (
                <>
                  <Button
                    color="default"
                    disabled={isDefaultFilter(filter)}
                    onClick={() =>
                      filterDispatcher(createFilterResetFilterAction())
                    }
                  >
                    <Clear fontSize="inherit" className="mr-1" />
                    <span>Clear</span>
                  </Button>
                  <SaveFilterButton />
                </>
              )}
              {owner && (
                <>
                  <Button
                    id="save-data-menu-button"
                    type="button"
                    aria-controls="save-data-menu"
                    aria-haspopup="true"
                    aria-expanded={saveDataMenuOpen ? "true" : undefined}
                    onClick={(event) =>
                      setSaveDataMenuAnchor(event.currentTarget)
                    }
                  >
                    <GetApp fontSize="inherit" className="mr-1" />
                    Download
                  </Button>
                  <Menu
                    id="save-data-menu"
                    anchorEl={saveDataMenuAnchor}
                    open={saveDataMenuOpen}
                    onClose={() => setSaveDataMenuAnchor(null)}
                    MenuListProps={{
                      "aria-labelledby": "save-data-menu-button",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setSaveDataMenuAnchor(null);
                        downloadData("CSV");
                      }}
                    >
                      Save as .csv file
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setSaveDataMenuAnchor(null);
                        downloadData("TXT");
                      }}
                    >
                      Save as .txt file
                    </MenuItem>
                  </Menu>
                </>
              )}
            </div>
          </div>
        </>
      )}
      <div className="border-b border-app-gray-08 space-y-2">
        <div className={filterMode === "edit" ? "block" : "hidden"}>
          <AssignmentsFilter
            onClose={() => (onClose ? onClose() : setFilterMode("saved"))}
          />
        </div>
        <div className={filterMode === "saved" ? "block" : "hidden"}>
          <SavedFilterPicker />
        </div>
      </div>
      <form
        ref={downloadDataForm}
        action={`/Groop/Download/${group.id}`}
        method="POST"
      >
        <input
          type="hidden"
          name="filter"
          value={JSON.stringify(
            GroopitFilterDisplay.convertToGroopitFilter(filter.filter),
          )}
        />
      </form>
    </>
  );
};
